import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaFileInvoice, FaArrowUp, FaArrowDown, FaMinus, FaExclamationTriangle, FaExternalLinkAlt, FaCheckCircle } from 'react-icons/fa';
import OpenTasksPanel from './OpenTasksPanel';
import OfferFunnelChart from './OfferFunnelChart';
import RevenueDonutChart from './RevenueDonutChart';
import RevenueMonthlyBarChart from './RevenueMonthlyBarChart';
import SalesOverviewService, { SalesOverview, DeltaDirection } from '../services/SalesOverviewService';
import InvoiceService, { Invoice } from '../services/InvoiceService';

interface KPICardProps {
  title: string;
  value: string;
  subtext: string;
  delta?: { direction: DeltaDirection; percent: number | null };
  highlight?: 'warning' | 'danger';
  onClick?: () => void;
}

function KPICard({ title, value, subtext, delta, highlight, onClick }: KPICardProps): React.JSX.Element {
  const [isHovered, setIsHovered] = React.useState(false);

  const getHighlightStyles = () => {
    if (highlight === 'danger') {
      return { borderLeft: '4px solid #dc2626', background: '#fef2f2' };
    }
    if (highlight === 'warning') {
      return { borderLeft: '4px solid #f59e0b', background: '#fffbeb' };
    }
    return {};
  };

  const getDeltaColor = (direction: DeltaDirection): string => {
    switch (direction) {
      case 'up':
        return '#059669';
      case 'down':
        return '#dc2626';
      default:
        return '#6b7280';
    }
  };

  const getDeltaIcon = (direction: DeltaDirection): React.ReactNode => {
    switch (direction) {
      case 'up':
        return <FaArrowUp size={10} />;
      case 'down':
        return <FaArrowDown size={10} />;
      default:
        return <FaMinus size={10} />;
    }
  };

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: 'white',
        borderRadius: '12px',
        padding: '20px 24px',
        boxShadow: isHovered ? '0 8px 24px rgba(0,0,0,0.12)' : '0 2px 8px rgba(0,0,0,0.08)',
        flex: 1,
        minWidth: '200px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s, box-shadow 0.2s',
        transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
        ...getHighlightStyles(),
      }}
    >
      <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px', fontWeight: 500 }}>
        {title}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
        <div style={{ fontSize: '28px', fontWeight: 700, color: highlight === 'danger' ? '#dc2626' : '#021a4a' }}>{value}</div>
        {delta && (delta.percent !== null || delta.direction !== 'flat') && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '13px',
              fontWeight: 600,
              color: getDeltaColor(delta.direction),
            }}
          >
            {getDeltaIcon(delta.direction)}
            {delta.percent !== null ? `${delta.percent.toFixed(1)}%` : (delta.direction === 'up' ? 'new' : '')}
          </div>
        )}
      </div>
      <div style={{ fontSize: '12px', color: '#888', marginTop: '6px' }}>{subtext}</div>
    </div>
  );
}

export default function SalesDashboardBoard(): React.JSX.Element {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const [overview, setOverview] = useState<SalesOverview | null>(null);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [overviewError, setOverviewError] = useState(false);
  const [overdueInvoices, setOverdueInvoices] = useState<Invoice[]>([]);
  const [loadingOverdue, setLoadingOverdue] = useState(true);

  useEffect(() => {
    SalesOverviewService.getOverview()
      .then((data) => {
        setOverview(data);
        setOverviewError(false);
      })
      .catch((err) => {
        console.error('Failed to load sales overview:', err);
        setOverviewError(true);
      })
      .finally(() => setLoadingOverview(false));
  }, []);

  const loadOverdueInvoices = useCallback(async () => {
    setLoadingOverdue(true);
    try {
      const data = await InvoiceService.getOverdue();
      setOverdueInvoices(data);
    } catch (err) {
      console.error('Failed to load overdue invoices:', err);
    } finally {
      setLoadingOverdue(false);
    }
  }, []);

  useEffect(() => {
    loadOverdueInvoices();
  }, [loadOverdueInvoices]);

  const getDaysOverdue = (dueDate: string): number => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    return Math.floor((today.getTime() - due.getTime()) / (1000 * 60 * 60 * 24));
  };

  return (
    <>
      {/* KPI Cards Row - Loading */}
      {loadingOverview && (
        <div
          style={{
            display: 'flex',
            gap: '16px',
            marginBottom: '24px',
            flexWrap: 'wrap',
          }}
        >
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px 24px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                flex: 1,
                minWidth: '200px',
              }}
            >
              <div style={{ width: '60%', height: '14px', background: '#e5e7eb', borderRadius: '4px', marginBottom: '12px' }} />
              <div style={{ width: '50%', height: '28px', background: '#e5e7eb', borderRadius: '4px', marginBottom: '8px' }} />
              <div style={{ width: '40%', height: '12px', background: '#e5e7eb', borderRadius: '4px' }} />
            </div>
          ))}
        </div>
      )}

      {/* KPI Cards Row - Error */}
      {!loadingOverview && overviewError && (
        <div
          style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '12px',
            padding: '16px 24px',
            marginBottom: '24px',
            color: '#991b1b',
            fontSize: '14px',
          }}
        >
          <FaExclamationTriangle style={{ marginRight: '8px' }} />
          Failed to load sales overview. Please refresh the page.
        </div>
      )}

      {/* KPI Cards Row - 5 Cards in Grid */}
      {!loadingOverview && !overviewError && overview && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: '16px',
            marginBottom: '24px',
          }}
        >
          {/* Karte 1: Open pipeline */}
          <KPICard
            title="Open pipeline"
            value={SalesOverviewService.formatCurrency(overview.offers?.amounts?.open_pipeline ?? 0)}
            subtext="Draft + Sent offers"
            onClick={() => navigate('/sales?tab=offers&pipeline=open')}
          />

          {/* Karte 2: Accepted offers */}
          <KPICard
            title="Accepted offers"
            value={SalesOverviewService.formatCurrency(overview.offers?.amounts?.ACCEPTED ?? 0)}
            subtext={`${overview.offers?.counts?.ACCEPTED ?? 0} offers`}
            onClick={() => navigate('/sales?tab=offers&status=ACCEPTED')}
          />

          {/* Karte 3: Invoiced this month */}
          <KPICard
            title="Invoiced this month"
            value={SalesOverviewService.formatCurrency(overview.invoices?.month?.current?.invoiced_gross ?? 0)}
            subtext="vs last month"
            delta={{
              direction: overview.invoices?.month?.delta?.direction_invoiced ?? 'flat',
              percent: overview.invoices?.month?.delta?.invoiced_percent ?? null,
            }}
            onClick={() => navigate('/sales?tab=invoices&status=SENT')}
          />

          {/* Karte 4: Paid this month */}
          <KPICard
            title="Paid this month"
            value={SalesOverviewService.formatCurrency(overview.invoices?.month?.current?.paid_gross ?? 0)}
            subtext="vs last month"
            delta={{
              direction: overview.invoices?.month?.delta?.direction_paid ?? 'flat',
              percent: overview.invoices?.month?.delta?.paid_percent ?? null,
            }}
            onClick={() => navigate('/sales?tab=invoices&status=PAID')}
          />

          {/* Karte 5: Recurring Revenue (MRR) */}
          <KPICard
            title="Recurring revenue"
            value={SalesOverviewService.formatCurrency(overview.serviceContracts?.mrr_gross ?? 0)}
            subtext={`${overview.serviceContracts?.active_count ?? 0} active contracts`}
          />
        </div>
      )}

      {/* Milestones Ready to Invoice Card */}
      {!loadingOverview && !overviewError && overview && (overview.milestones?.amounts?.ready_for_invoice ?? 0) > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 2fr',
            gap: '16px',
            marginBottom: '24px',
          }}
        >
          <div
            onClick={() => navigate('/sales?tab=offers&filter=has_payment_plan')}
            style={{
              background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
              borderRadius: '12px',
              padding: '20px 24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              border: '1px solid #6ee7b7',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <FaCheckCircle size={16} color="#059669" />
              <span style={{ fontSize: '13px', color: '#065f46', fontWeight: 500 }}>
                Milestones ready to invoice
              </span>
            </div>
            <div style={{ fontSize: '32px', fontWeight: 700, color: '#065f46', marginBottom: '8px' }}>
              {SalesOverviewService.formatCurrency(overview.milestones.amounts.ready_for_invoice)}
            </div>
            <div style={{ fontSize: '13px', color: '#047857' }}>
              {overview.milestones.counts.READY_FOR_INVOICE} milestone{overview.milestones.counts.READY_FOR_INVOICE !== 1 ? 's' : ''}
            </div>
            <button
              style={{
                marginTop: '12px',
                padding: '8px 16px',
                background: 'white',
                border: '1px solid #059669',
                borderRadius: '6px',
                color: '#059669',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              View details <FaExternalLinkAlt size={10} />
            </button>
          </div>

          {/* Milestone Status Overview */}
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px 24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <div style={{ fontSize: '13px', color: '#666', marginBottom: '16px', fontWeight: 500 }}>
              Milestone status overview
            </div>
            <div style={{ display: 'flex', gap: '16px' }}>
              {[
                { label: 'Planned', count: overview.milestones.counts.PLANNED, amount: overview.milestones.amounts.planned, color: '#6b7280' },
                { label: 'Ready', count: overview.milestones.counts.READY_FOR_INVOICE, amount: overview.milestones.amounts.ready_for_invoice, color: '#059669' },
                { label: 'Invoiced', count: overview.milestones.counts.INVOICED, amount: overview.milestones.amounts.invoiced, color: '#f59e0b' },
                { label: 'Paid', count: overview.milestones.counts.PAID, amount: overview.milestones.amounts.paid, color: '#3b82f6' },
              ].map((item) => (
                <div
                  key={item.label}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#f8fafc',
                    borderRadius: '8px',
                    borderLeft: `3px solid ${item.color}`,
                  }}
                >
                  <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>{item.label}</div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#021a4a' }}>
                    {SalesOverviewService.formatCurrency(item.amount)}
                  </div>
                  <div style={{ fontSize: '11px', color: '#888' }}>{item.count} milestones</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Payment Plans Card - Only shown when there are payment plans */}
      {!loadingOverview && !overviewError && overview && overview.paymentPlans.plans_count > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 2fr',
            gap: '16px',
            marginBottom: '24px',
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px 24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px', fontWeight: 500 }}>
              Planned revenue (payment plans)
            </div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#021a4a', marginBottom: '8px' }}>
              {SalesOverviewService.formatCurrency(overview.paymentPlans.planned_gross_total)}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              <span style={{ color: '#059669', fontWeight: 600 }}>
                Invoiced: {SalesOverviewService.formatCurrency(overview.paymentPlans.planned_gross_invoiced)}
              </span>
              {' · '}
              <span style={{ color: '#f59e0b', fontWeight: 600 }}>
                Remaining: {SalesOverviewService.formatCurrency(overview.paymentPlans.planned_gross_remaining)}
              </span>
            </div>
            <div style={{ fontSize: '11px', color: '#999', marginTop: '8px' }}>
              {overview.paymentPlans.plans_count} plans · {overview.paymentPlans.milestones_total} milestones
              ({overview.paymentPlans.milestones_invoiced} invoiced, {overview.paymentPlans.milestones_remaining} open)
            </div>
          </div>

          {/* Payment Plan Donut */}
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px 24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width="180" height="100" viewBox="0 0 180 100">
              {(() => {
                const total = overview.paymentPlans.planned_gross_total || 1;
                const invoiced = overview.paymentPlans.planned_gross_invoiced;
                const invoicedPercent = (invoiced / total) * 100;
                const radius = 45;
                const cx = 90;
                const cy = 50;
                const circumference = Math.PI * radius;
                const invoicedArc = (invoicedPercent / 100) * circumference;

                return (
                  <>
                    {/* Background arc (remaining) */}
                    <path
                      d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
                      fill="none"
                      stroke="#fef3c7"
                      strokeWidth="12"
                    />
                    {/* Invoiced arc */}
                    <path
                      d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
                      fill="none"
                      stroke="#059669"
                      strokeWidth="12"
                      strokeDasharray={`${invoicedArc} ${circumference}`}
                    />
                    {/* Center text */}
                    <text x={cx} y={cy + 5} textAnchor="middle" fontSize="16" fontWeight="700" fill="#021a4a">
                      {Math.round(invoicedPercent)}%
                    </text>
                    <text x={cx} y={cy + 20} textAnchor="middle" fontSize="10" fill="#666">
                      invoiced
                    </text>
                  </>
                );
              })()}
            </svg>
            <div style={{ marginLeft: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '2px', background: '#059669' }} />
                <span style={{ fontSize: '12px', color: '#666' }}>Invoiced</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '2px', background: '#fef3c7' }} />
                <span style={{ fontSize: '12px', color: '#666' }}>Remaining</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Offer Funnel + Revenue Donut Row */}
      {!loadingOverview && !overviewError && overview && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
            marginBottom: '24px',
          }}
        >
          {/* Offer Funnel Card */}
          <OfferFunnelChart
            counts={overview.offers?.counts ?? { DRAFT: 0, SENT: 0, ACCEPTED: 0, REJECTED: 0, EXPIRED: 0 }}
            amounts={overview.offers?.amounts}
            conversionRate={overview.offers?.conversion_rate}
          />

          {/* Revenue Donut Chart - Invoiced vs Accepted (not invoiced) */}
          <RevenueDonutChart
            acceptedTotal={overview.invoicing?.accepted_total_gross ?? 0}
            invoicedAgainst={overview.invoicing?.invoiced_against_offers_gross ?? 0}
            remaining={overview.invoicing?.remaining_to_invoice_gross ?? 0}
          />
        </div>
      )}

      {/* Monthly Performance Bar Chart */}
      {!loadingOverview && !overviewError && overview && (
        <div style={{ marginBottom: '24px' }}>
          <RevenueMonthlyBarChart data={overview.invoices.history} />
        </div>
      )}

      {/* Bottom Panels: Open/Overdue Invoices (left) + Tasks (right) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '16px',
          marginBottom: '24px',
        }}
      >
        {/* Left: Open & Overdue Invoices Panel */}
        <div
          style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px 24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#021a4a', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaFileInvoice size={16} />
            Open & Overdue Invoices
          </div>

          {/* Summary Row */}
          {!loadingOverview && overview && (
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <div
                onClick={() => navigate('/sales?tab=invoices&status=SENT')}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Open</div>
                <div style={{ fontSize: '20px', fontWeight: 700, color: '#021a4a' }}>
                  {SalesOverviewService.formatCurrency(overview.invoices?.amounts?.open ?? 0)}
                </div>
                <div style={{ fontSize: '11px', color: '#888' }}>
                  {(overview.invoices?.counts?.total ?? 0) - (overview.invoices?.counts?.PAID ?? 0) - (overview.invoices?.counts?.CANCELLED ?? 0)} invoices
                </div>
              </div>
              <div
                onClick={() => setSearchParams({ tab: 'invoices', filter: 'overdue' })}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                  Overdue
                </div>
                <div style={{ fontSize: '20px', fontWeight: 700, color: (overview.invoices?.amounts?.overdue ?? 0) > 0 ? '#dc2626' : '#021a4a' }}>
                  {SalesOverviewService.formatCurrency(overview.invoices?.amounts?.overdue ?? 0)}
                </div>
                <div style={{ fontSize: '11px', color: '#888' }}>
                  {overview.invoices?.counts?.overdue ?? 0} invoices
                </div>
              </div>
            </div>
          )}

          {/* Top 3-5 Overdue List */}
          {!loadingOverdue && overdueInvoices?.length > 0 && (
            <>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#666', marginBottom: '8px' }}>
                Top Overdue
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {overdueInvoices.slice(0, 3).map((invoice) => {
                  const daysOverdue = getDaysOverdue(invoice.due_date);
                  return (
                    <div
                      key={invoice.id}
                      onClick={() => navigate(`/invoices/${invoice.id}`)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 12px',
                        background: '#f8fafc',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        border: '1px solid #e5e7eb',
                      }}
                    >
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#021a4a' }}>
                          {invoice.invoice_number}
                        </div>
                        <div style={{ fontSize: '11px', color: '#666' }}>
                          {invoice.partner_name}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#dc2626' }}>
                          {InvoiceService.formatCurrency(invoice.gross_total)}
                        </div>
                        <span
                          style={{
                            padding: '2px 6px',
                            borderRadius: '10px',
                            fontSize: '10px',
                            fontWeight: 600,
                            background: '#f1f5f9',
                            color: '#dc2626',
                          }}
                        >
                          {daysOverdue}d overdue
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* View all link */}
          <button
            onClick={() => setSearchParams({ tab: 'invoices' })}
            style={{
              marginTop: '16px',
              padding: '10px 16px',
              width: '100%',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              background: 'white',
              color: '#3b82f6',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
          >
            View all invoices <FaExternalLinkAlt size={10} />
          </button>
        </div>

        {/* Right: Open Tasks Panel */}
        <OpenTasksPanel maxItems={5} showViewAll={true} />
      </div>
    </>
  );
}
